import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';

const fmt     = (dt) => dt ? new Date(dt).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';
const fmtSecs = (s) => { if (!s) return '—'; const h=Math.floor(s/3600); const m=Math.floor((s%3600)/60); const sec=s%60; return h>0?`${h}h ${m}m`:`${m}m ${sec}s`; };
const stripHtml = (h) => h?.replace(/<[^>]+>/g,'') || '';

const GRADE_BG = {'A+':'#dcfce7','A':'#d1fae5','B':'#d9f99d','C':'#fef08a','D':'#fed7aa','F':'#fee2e2','—':'#f3f4f6'};
const GRADE_FG = {'A+':'#15803d','A':'#166534','B':'#4d7c0f','C':'#854d0e','D':'#c2410c','F':'#dc2626','—':'#6b7280'};
const SEV_COL  = {low:'#6b7280',medium:'#f59e0b',high:'#ef4444',critical:'#991b1b'};
const BASE_CSS = `*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:20px}h1{font-size:20px;margin-bottom:4px}.sub{color:#555;font-size:13px;margin-bottom:20px}.sec{border:1px solid #ddd;border-radius:8px;padding:14px;margin-bottom:16px}h3{font-size:13px;font-weight:700;margin-bottom:10px;padding-bottom:6px;border-bottom:1px solid #eee}.row{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #f0f0f0;font-size:11px}.row:last-child{border:none}.rl{color:#666}.rv{font-weight:600}table{width:100%;border-collapse:collapse;font-size:11px}th{background:#1e293b;color:#fff;padding:6px 8px;text-align:left;font-size:10px}td{padding:5px 8px;border-bottom:1px solid #e5e7eb}tr:nth-child(even){background:#f9fafb}.bk{padding:2px 8px;border-radius:12px;font-size:10px;font-weight:700;display:inline-block}.footer{margin-top:16px;font-size:10px;color:#888;text-align:right}@media print{@page{margin:12mm}body{padding:0}}`;

// ── Tooltip ───────────────────────────────────────────────────────────────────
function Tip({ text, children }) {
  const [v, sv] = useState(false);
  return (
    <span style={{ position:'relative', display:'inline-flex', alignItems:'center' }}
      onMouseEnter={()=>sv(true)} onMouseLeave={()=>sv(false)}>
      {children}
      {v && <span style={{ position:'absolute', bottom:'calc(100% + 6px)', left:'50%', transform:'translateX(-50%)', background:'#1e293b', color:'#fff', padding:'5px 10px', borderRadius:6, fontSize:11, whiteSpace:'nowrap', zIndex:9999, pointerEvents:'none', boxShadow:'0 4px 12px rgba(0,0,0,.4)' }}>{text}</span>}
    </span>
  );
}

function InfoRow({ label, val, color, tip }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
      <span style={{ color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:4 }}>
        {label}{tip && <Tip text={tip}><span style={{ cursor:'help', color:'var(--text-muted)', fontSize:12 }}> ⓘ</span></Tip>}
      </span>
      <span style={{ fontWeight:600, color:color||'var(--text-primary)', textAlign:'right', maxWidth:'60%' }}>{val}</span>
    </div>
  );
}

function openPrint(html) {
  const pw = window.open('', '_blank');
  pw.document.write(html); pw.document.close();
  pw.onload = () => { pw.focus(); pw.print(); };
}

// ── PDF section builders ──────────────────────────────────────────────────────
function headerHTML(d, terminationType, pct) {
  return `<div style="display:grid;grid-template-columns:1fr auto;gap:16px;margin-bottom:16px;align-items:start">
    <div class="sec" style="margin:0">
      <div style="font-size:17px;font-weight:700;margin-bottom:4px">${d.full_name}</div>
      <div style="color:#555;font-size:12px;margin-bottom:8px">${d.email}</div>
      ${d.ip_address?`<div style="font-size:11px;color:#888;font-family:monospace;margin-bottom:8px">🌐 IP: ${d.ip_address}</div>`:''}
      <span class="bk" style="background:${d.status==='submitted'?'#dcfce7':'#fee2e2'};color:${d.status==='submitted'?'#16a34a':'#dc2626'}">${terminationType}</span>
      ${d.passed===true?'<span class="bk" style="background:#dcfce7;color:#16a34a;margin-left:4px">✓ PASSED</span>':''}
      ${d.passed===false?'<span class="bk" style="background:#fee2e2;color:#dc2626;margin-left:4px">✗ FAILED</span>':''}
    </div>
    <div style="border-radius:8px;padding:18px 24px;text-align:center;border:2px solid ${GRADE_FG[d.grade]||'#9ca3af'};background:${GRADE_BG[d.grade]||'#f3f4f6'}">
      <div style="font-size:48px;font-weight:900;line-height:1;color:${GRADE_FG[d.grade]||'#6b7280'}">${d.grade}</div>
      <div style="font-size:18px;font-weight:700;color:${GRADE_FG[d.grade]}">${pct.toFixed(1)}%</div>
      <div style="font-size:12px;color:#6b7280;margin-top:2px">${d.score??'—'} / ${d.total_marks}</div>
    </div>
  </div>`;
}

function overviewHTML(d, terminationType, pct) {
  return `<div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
    <div class="sec"><h3>📋 Exam Details</h3>
      <div class="row"><span class="rl">Exam</span><span class="rv">${d.exam_title}</span></div>
      <div class="row"><span class="rl">Organization</span><span class="rv">${d.org_name}</span></div>
      <div class="row"><span class="rl">Duration</span><span class="rv">${d.duration_minutes} min</span></div>
      <div class="row"><span class="rl">Total Marks</span><span class="rv">${d.total_marks}</span></div>
      <div class="row"><span class="rl">Passing Marks</span><span class="rv">${d.passing_marks}</span></div>
      <div class="row"><span class="rl">Started At</span><span class="rv">${fmt(d.started_at)}</span></div>
      <div class="row"><span class="rl">Submitted At</span><span class="rv">${fmt(d.submitted_at)}</span></div>
      <div class="row"><span class="rl">Time Spent</span><span class="rv">${d.time_spent_seconds?Math.round(d.time_spent_seconds/60)+' min':'—'}</span></div>
      <div class="row"><span class="rl">IP Address</span><span class="rv">${d.ip_address||'—'}</span></div>
    </div>
    <div class="sec"><h3>🎯 Performance</h3>
      <div class="row"><span class="rl">Score</span><span class="rv" style="color:${d.passed?'#16a34a':'#dc2626'}">${d.score??'—'} / ${d.total_marks}</span></div>
      <div class="row"><span class="rl">Percentage</span><span class="rv">${pct.toFixed(1)}%</span></div>
      <div class="row"><span class="rl">Grade</span><span class="rv" style="color:${GRADE_FG[d.grade]||'#6b7280'}">${d.grade}</span></div>
      <div class="row"><span class="rl">Result</span><span class="rv" style="color:${d.passed===true?'#16a34a':'#dc2626'}">${d.passed===true?'PASS':d.passed===false?'FAIL':'—'}</span></div>
      <div class="row"><span class="rl">Sessions</span><span class="rv">${d.session_count||1}</span></div>
      <div class="row"><span class="rl">Page Refreshes</span><span class="rv" style="color:${d.refresh_count>0?'#dc2626':'inherit'}">${d.refresh_count||0}</span></div>
      <div class="row"><span class="rl">Violations</span><span class="rv" style="color:${d.total_warnings>0?'#dc2626':'inherit'}">${d.total_warnings||0}</span></div>
      <div class="row"><span class="rl">Trust Score</span><span class="rv">${d.trust_score?parseFloat(d.trust_score).toFixed(1)+'%':'—'}</span></div>
      <div class="row"><span class="rl">Termination</span><span class="rv">${terminationType}</span></div>
      <div class="row"><span class="rl">Verdict</span><span class="rv">${d.final_verdict||'pending'}</span></div>
    </div>
  </div>`;
}

function violationsHTML(d) {
  if (!d.violations?.length) return `<div class="sec" style="text-align:center;padding:24px;color:#888">✅ No violations recorded</div>`;
  const rows = d.violations.map((v,i) => `<tr><td>${i+1}</td><td>${fmt(v.timestamp)}</td><td>${(v.event_type||'').replace(/_/g,' ')}</td><td style="color:${SEV_COL[v.severity]||'#6b7280'};font-weight:700">${v.severity}</td><td>${v.description||'—'}</td></tr>`).join('');
  return `<div class="sec"><h3>⚠ Violations (${d.violations.length})</h3><table><thead><tr><th>#</th><th>Time</th><th>Event</th><th>Severity</th><th>Description</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

function answersHTML(d, pct) {
  if (!d.answers?.length) return `<div class="sec" style="text-align:center;padding:24px;color:#888">📝 No answers recorded</div>`;
  const items = d.answers.map((a,i) => `
    <div style="border:1px solid #ddd;border-radius:6px;padding:10px;margin-bottom:10px;border-left:3px solid ${a.is_correct?'#16a34a':'#dc2626'};page-break-inside:avoid">
      <div style="display:flex;justify-content:space-between;margin-bottom:6px">
        <strong>Q${i+1}. ${stripHtml(a.question_text)?.slice(0,200)}</strong>
        <span class="bk" style="background:${a.is_correct?'#dcfce7':'#fee2e2'};color:${a.is_correct?'#16a34a':'#dc2626'};flex-shrink:0;margin-left:8px">${a.is_correct?`✓ +${a.marks_awarded}`:`✗ 0`}</span>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:11px">
        <div style="padding:5px 8px;background:#fef2f2;border-radius:4px;border:1px solid #fecaca"><div style="color:#888;margin-bottom:2px">Student's Answer</div><strong>${a.answer||'Not answered'}</strong></div>
        <div style="padding:5px 8px;background:#f0fdf4;border-radius:4px;border:1px solid #86efac"><div style="color:#888;margin-bottom:2px">Correct Answer</div><strong style="color:#16a34a">${a.correct_answer||'—'}</strong></div>
      </div>
    </div>`).join('');
  const correct = d.answers.filter(a=>a.is_correct).length;
  return `<div class="sec"><h3>📝 Answers — ${correct}/${d.answers.length} correct · Score: ${d.score}/${d.total_marks} (${pct.toFixed(1)}%)</h3>${items}</div>`;
}

function wrapPage(title, body) {
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>${title}</title><style>${BASE_CSS}</style></head><body>${body}<div class="footer">ExamAI · ${title} · ${new Date().toLocaleDateString()}</div></body></html>`;
}

// ─── ExamResultReport ─────────────────────────────────────────────────────────
export default function ExamResultReport() {
  const { examId, attemptId } = useParams();
  const navigate = useNavigate();
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [permError, setPermError] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [tab,       setTab]       = useState('overview');

  useEffect(() => {
    axios.get(`/api/exams/${examId}/results/${attemptId}`)
      .then(r => setData(r.data))
      .catch(err => {
        const msg = err.response?.data?.error;
        if (err.response?.status === 403) setPermError(msg || 'Access denied');
        else toast.error(msg || 'Failed to load result');
      })
      .finally(() => setLoading(false));
  }, [examId, attemptId]);

  if (permError) return (
    <div className="layout"><Sidebar />
      <main className="main-content">
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', textAlign:'center', padding:40 }}>
          <div style={{ fontSize:56, marginBottom:20 }}>🔒</div>
          <div style={{ fontWeight:700, fontSize:20, marginBottom:10, color:'var(--accent-warning)' }}>Access Restricted</div>
          <div style={{ fontSize:15, color:'var(--text-secondary)', maxWidth:420, lineHeight:1.8 }}>{permError}</div>
          <button className="btn btn-secondary" style={{ marginTop:24 }} onClick={() => window.history.back()}>← Go Back</button>
        </div>
      </main>
    </div>
  );

  if (loading) return (
    <div className="layout"><Sidebar />
      <main className="main-content">
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:400, gap:16 }}>
          <div className="spinner" style={{ width:44, height:44, borderWidth:4 }} />
          <div style={{ fontSize:14, color:'var(--text-secondary)' }}>Loading result data...</div>
        </div>
      </main>
    </div>
  );

  if (!data) return <div className="loading-screen"><p>Result not found or access denied</p></div>;

  const d   = data;
  const pct = parseFloat(d.percentage) || 0;
  const terminationType =
    d.status === 'submitted'                       ? 'Submitted' :
    d.termination_reason === 'student_refresh'     ? 'Terminated (Page Refresh)' :
    d.termination_reason?.includes('Time expired') ? 'Terminated (Time Up)' :
    d.auto_terminated                              ? 'Auto-Terminated (Violations)' :
    d.termination_reason                           ? `Terminated: ${d.termination_reason}` :
    d.status === 'terminated'                      ? 'Terminated by Proctor' : 'In Progress';

  const doExport = (section) => {
    setExporting(section);
    const title = `${d.full_name} — ${d.exam_title}`;
    let html;
    if (section === 'full') {
      html = wrapPage(`Full Report — ${title}`,
        `<h1>Full Exam Result Report</h1><div class="sub">${d.exam_title} · ${d.org_name} · Generated ${new Date().toLocaleString()}</div>` +
        headerHTML(d, terminationType, pct) +
        overviewHTML(d, terminationType, pct) +
        `<div style="page-break-before:always"></div>` +
        violationsHTML(d) +
        `<div style="page-break-before:always"></div>` +
        answersHTML(d, pct)
      );
    } else if (section === 'overview') {
      html = wrapPage(`Result Overview — ${title}`,
        `<h1>Result Overview</h1><div class="sub">${d.exam_title} · ${d.org_name} · Generated ${new Date().toLocaleString()}</div>` +
        headerHTML(d, terminationType, pct) + overviewHTML(d, terminationType, pct)
      );
    } else if (section === 'violations') {
      html = wrapPage(`Violations — ${title}`,
        `<h1>Violation Report</h1><div class="sub">${d.full_name} · ${d.exam_title} · Generated ${new Date().toLocaleString()}</div>` +
        violationsHTML(d)
      );
    } else {
      html = wrapPage(`Answer Sheet — ${title}`,
        `<h1>Answer Sheet</h1><div class="sub">${d.full_name} · ${d.exam_title} · Score: ${d.score}/${d.total_marks} (${pct.toFixed(1)}%) · Generated ${new Date().toLocaleString()}</div>` +
        answersHTML(d, pct)
      );
    }
    openPrint(html);
    setTimeout(() => setExporting(null), 1500);
  };

  return (
    <div className="layout">
      <Sidebar />
      <main className="main-content">

        {/* Header */}
        <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
          <div>
            <button onClick={() => navigate(-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-secondary)', fontSize:13, marginBottom:6, padding:0 }}>← Back</button>
            <h1 className="page-title" style={{ margin:0 }}>📋 Exam Result</h1>
            <p className="page-subtitle" style={{ margin:0 }}>{d.exam_title} · {d.org_name}</p>
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <Tip text="Export currently visible tab as PDF">
              <button className="btn btn-secondary btn-sm" disabled={!!exporting} onClick={() => doExport(tab)}>
                {exporting === tab ? '⏳...' : `📄 Export ${tab.charAt(0).toUpperCase()+tab.slice(1)}`}
              </button>
            </Tip>
            <Tip text="Export all 3 tabs in one complete PDF">
              <button className="btn btn-primary" disabled={!!exporting} onClick={() => doExport('full')}>
                {exporting === 'full' ? '⏳ Generating...' : '📄 Export Full Report'}
              </button>
            </Tip>
          </div>
        </div>

        {/* Student + Grade */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:16, marginBottom:20 }}>
          <div className="card">
            <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>{d.full_name}</div>
            <div style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:6 }}>{d.email}</div>
            {d.ip_address && (
              <Tip text="IP address captured when student started the exam">
                <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:10, fontFamily:'var(--font-mono)', cursor:'default' }}>
                  🌐 {d.ip_address} ⓘ
                </div>
              </Tip>
            )}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <span className={`badge badge-${d.status==='submitted'?'green':d.status==='terminated'?'red':'yellow'}`}>{terminationType}</span>
              {d.auto_terminated && <span className="badge badge-red">Auto-Terminated</span>}
              {d.passed === true  && <span className="badge badge-green">✓ Passed</span>}
              {d.passed === false && <span className="badge badge-red">✗ Failed</span>}
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'20px 28px', background:GRADE_BG[d.grade]||'#f3f4f6', borderRadius:12, border:`2px solid ${GRADE_FG[d.grade]||'#9ca3af'}` }}>
            <div style={{ fontSize:52, fontWeight:900, lineHeight:1, color:GRADE_FG[d.grade]||'#6b7280' }}>{d.grade}</div>
            <div style={{ fontSize:18, fontWeight:700, marginTop:4, color:GRADE_FG[d.grade] }}>{pct.toFixed(1)}%</div>
            <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>{d.score ?? '—'} / {d.total_marks}</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:8, marginBottom:20 }}>
          {[['overview','📊 Overview'],['violations',`⚠ Violations (${d.violations?.length||0})`],['answers',`📝 Answers (${d.answers?.length||0})`]].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)} className="btn btn-secondary btn-sm"
              style={tab===k ? { borderColor:'var(--accent-primary)', color:'var(--accent-primary)', background:'rgba(79,142,247,0.1)' } : {}}>
              {l}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === 'overview' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <div className="card">
              <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>📋 Exam Details</div>
              <InfoRow label="Exam"          val={d.exam_title} />
              <InfoRow label="Organization"  val={d.org_name} />
              <InfoRow label="Duration"      val={`${d.duration_minutes} min`} />
              <InfoRow label="Total Marks"   val={d.total_marks} />
              <InfoRow label="Passing Marks" val={d.passing_marks} />
              <InfoRow label="Started At"    val={fmt(d.started_at)} />
              <InfoRow label="Submitted At"  val={fmt(d.submitted_at)} />
              <InfoRow label="Time Spent"    val={fmtSecs(d.time_spent_seconds)} />
              <InfoRow label="IP Address"    val={d.ip_address||'—'} tip="Captured when student started exam" />
            </div>
            <div className="card">
              <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>🎯 Performance</div>
              <InfoRow label="Score"         val={`${d.score??'—'} / ${d.total_marks}`} color={pct>=parseFloat(d.passing_marks/d.total_marks*100)?'var(--accent-success)':'var(--accent-danger)'} />
              <InfoRow label="Percentage"    val={`${pct.toFixed(1)}%`} />
              <InfoRow label="Grade"         val={d.grade} color={GRADE_FG[d.grade]} />
              <InfoRow label="Result"        val={d.passed===true?'PASS':d.passed===false?'FAIL':'—'} color={d.passed===true?'var(--accent-success)':'var(--accent-danger)'} />
              <InfoRow label="Sessions"      val={d.session_count||1} tip="How many times the exam was started or resumed" />
              <InfoRow label="Page Refreshes" val={d.refresh_count||0} color={d.refresh_count>0?'var(--accent-danger)':undefined} tip="Each refresh terminates the exam" />
              <InfoRow label="Violations"    val={d.total_warnings||0} color={d.total_warnings>0?'var(--accent-danger)':undefined} />
              <InfoRow label="Trust Score"   val={d.trust_score?`${parseFloat(d.trust_score).toFixed(1)}%`:'—'} color={parseFloat(d.trust_score)<60?'var(--accent-danger)':'var(--accent-success)'} tip="100% = no proctoring violations" />
              <InfoRow label="Termination"   val={terminationType} />
              <InfoRow label="Verdict"       val={d.final_verdict||'pending'} />
            </div>
          </div>
        )}

        {/* Violations */}
        {tab === 'violations' && (
          <div className="card" style={{ padding:0 }}>
            {!d.violations?.length ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--text-secondary)' }}>
                <div style={{ fontSize:32, marginBottom:10 }}>✅</div>
                <div style={{ fontWeight:600 }}>No violations recorded for this student</div>
              </div>
            ) : (
              <div className="table-container" style={{ border:'none' }}>
                <table>
                  <thead><tr><th>#</th><th>Time</th><th>Event</th><th>Severity</th><th>Description</th></tr></thead>
                  <tbody>
                    {d.violations.map((v,i) => (
                      <tr key={i}>
                        <td style={{ color:'var(--text-muted)', fontSize:12 }}>{i+1}</td>
                        <td style={{ fontSize:12 }}>{fmt(v.timestamp)}</td>
                        <td style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>{(v.event_type||'').replace(/_/g,' ')}</td>
                        <td><span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700, background:`${SEV_COL[v.severity]||'#6b7280'}22`, color:SEV_COL[v.severity]||'#6b7280', textTransform:'uppercase' }}>{v.severity}</span></td>
                        <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{v.description||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Answers */}
        {tab === 'answers' && (
          <div>
            {!d.answers?.length ? (
              <div className="card" style={{ textAlign:'center', padding:40, color:'var(--text-secondary)' }}>
                <div style={{ fontSize:32, marginBottom:10 }}>📝</div>
                <div>No answers recorded</div>
              </div>
            ) : (
              <>
                {d.answers.map((a,i) => (
                  <div key={a.question_id||i} className="card" style={{ marginBottom:12, borderLeft:`3px solid ${a.is_correct?'var(--accent-success)':'var(--accent-danger)'}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                      <div style={{ fontWeight:600, fontSize:14, flex:1 }}>Q{i+1}. <span style={{ fontWeight:400 }} dangerouslySetInnerHTML={{ __html:a.question_text?.slice(0,300) }} /></div>
                      <div style={{ display:'flex', gap:8, flexShrink:0, marginLeft:8, alignItems:'center' }}>
                        <span style={{ fontSize:12, color:'var(--text-muted)' }}>{a.marks}m</span>
                        <span style={{ padding:'2px 10px', borderRadius:20, fontSize:12, fontWeight:700, background:a.is_correct?'rgba(16,185,129,0.12)':'rgba(239,68,68,0.1)', color:a.is_correct?'var(--accent-success)':'var(--accent-danger)' }}>
                          {a.is_correct ? `✓ +${a.marks_awarded}` : `✗ 0`}
                        </span>
                      </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize:13 }}>
                      <div style={{ padding:'8px 12px', background:'rgba(239,68,68,0.06)', borderRadius:8, border:'1px solid rgba(239,68,68,0.2)' }}>
                        <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:3 }}>Student's Answer</div>
                        <div style={{ fontWeight:600 }}>{a.answer || <em style={{ color:'var(--text-muted)' }}>Not answered</em>}</div>
                      </div>
                      <div style={{ padding:'8px 12px', background:'rgba(16,185,129,0.06)', borderRadius:8, border:'1px solid rgba(16,185,129,0.2)' }}>
                        <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:3 }}>Correct Answer</div>
                        <div style={{ fontWeight:600, color:'var(--accent-success)' }}>{a.correct_answer||'—'}</div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="card" style={{ display:'flex', justifyContent:'space-between', padding:'14px 20px' }}>
                  <div style={{ fontWeight:600, fontSize:14 }}>Score Summary</div>
                  <div style={{ fontSize:14 }}>
                    <span style={{ color:'var(--accent-success)', fontWeight:700 }}>{d.answers.filter(a=>a.is_correct).length} correct</span>
                    {' · '}
                    <span style={{ color:'var(--accent-danger)' }}>{d.answers.filter(a=>!a.is_correct).length} wrong</span>
                    {' · '}
                    <span style={{ fontWeight:700 }}>{d.score}/{d.total_marks} ({pct.toFixed(1)}%)</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

      </main>
    </div>
  );
}
